import { Suspense } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { Store, ChevronLeft } from 'lucide-react';

import { checkDashboardAccess } from '@/lib/route-utils';
import Header from '@/components/layout/header/header-layout';
import Sidebar from '@/components/layout/nav/sidebar-layout';
import Content from '@/components/layout/content/content-layout';
import { DashboardLayoutProvider } from '@/stores/context/dashboard-layout-context';
import { useSidebarCollapseStore } from '@/stores/sidebar-collapse';
import { useStore } from '@/stores';
import { BannerAnnouncement } from '@/components/shared/banner-announcement';
import { useBannerAnnouncementStore } from '@/stores/banner-announcement';
import { useAccountGroup } from '@/features/account-group/api/use-account-group';
import { Button } from '@/components/ui/button';
import { env } from '@/config/env';
import { cn } from '@/lib/utils';

// import { PopupAnnouncement } from '../shared/popup-announcement';

function ActiveBusinessBanner() {
  const profile = useStore((state) => state.profile);
  const selectedBusiness = useStore((state) => state.selectedBusiness);

  const isMainGroup = profile?.is_group === true && profile?.business_group_status === 'main';

  const { data: accountGroupData } = useAccountGroup(
    { page: 1, limit: 1 },
    { enabled: isMainGroup && selectedBusiness !== null },
  );

  const mainBusinessId = accountGroupData?.data?.main_business_id;
  const mainBusiness = accountGroupData?.data?.businesses?.find(
    (b) => String(b.id) === String(mainBusinessId),
  );

  const ownBusinessId = profile?.business.id ? String(profile.business.id) : null;
  const isViewingOther = selectedBusiness !== null && selectedBusiness.id !== ownBusinessId;
  const isViewingMain =
    mainBusinessId !== undefined && selectedBusiness?.id === String(mainBusinessId);

  const setSelectedBusiness = useStore((state) => state.setSelectedBusiness);

  const handleBackToMain = () => {
    if (!mainBusiness) return;
    setSelectedBusiness({ id: String(mainBusiness.id), name: mainBusiness.name });
  };

  if (!isViewingOther || isViewingMain || !selectedBusiness) return null;

  return (
    <div className="bg-primary mb-3 flex flex-col gap-3 rounded-md px-4 py-4 text-sm text-white sm:flex-row sm:items-center sm:justify-between sm:gap-2 sm:py-5">
      <div className="flex flex-row items-center gap-2">
        <Store className="h-4 w-4 shrink-0" />
        <span>
          You are viewing <span className="font-semibold">{selectedBusiness.name}</span> — a sub
          business in your Account Group.
        </span>
      </div>
      {mainBusiness && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleBackToMain}
          className="text-primary w-full shrink-0 gap-1.5 bg-white hover:bg-white/80 sm:w-auto"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to <span className="font-semibold">{mainBusiness.name}</span>
        </Button>
      )}
    </div>
  );
}

function DashboardLayout({ children }: { children: React.ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const roleAccess = useStore((state) => state.merchantAccess);
  const profile = useStore((state) => state.profile);
  const isSidebarCollapsed = useSidebarCollapseStore((state) => state.isCollapsed);
  const hasAccess = checkDashboardAccess(pathname, roleAccess);
  const showSandboxBar = env.sandboxMode === 'true';
  const bannerHeight = useBannerAnnouncementStore((s) => s.bannerHeight);

  const sandboxBarHeight = showSandboxBar ? 36 : 0;
  const headerTopOffset = sandboxBarHeight + bannerHeight;
  const headerHeight = 130;
  const contentPaddingTop = headerHeight + headerTopOffset;
  const sidebarTopOffset = contentPaddingTop;

  // const { shownPopupAnnountcement, setPopupAnnountcement } = useStore();

  return (
    <DashboardLayoutProvider>
      <BannerAnnouncement />
      <div className="flex min-h-screen w-full">
        <Header topOffset={headerTopOffset} />

        <div className="flex w-full flex-row gap-4 px-4" style={{ paddingTop: contentPaddingTop }}>
          <Sidebar topOffset={sidebarTopOffset} mobileTopOffset={headerTopOffset} />
          <Suspense>
            <div
              className={cn(
                'flex min-h-screen w-full flex-1 flex-col pb-5 transition-[padding-left] duration-200',
                isSidebarCollapsed ? 'md:pl-[88px]' : 'md:pl-[270px]',
              )}
            >
              {/* info business */}
              <ActiveBusinessBanner />
              <Content
                statusRegistered={(profile && profile.business.status) || ''}
                isAccountGroup={profile?.is_group ?? undefined}
              >
                {hasAccess ? children : <div>No access</div>}
              </Content>
            </div>
          </Suspense>
        </div>
      </div>
      {/* USE FOR POPUP ANNOUNTCEMENT */}
      {/* <PopupAnnouncement
        opened={shownPopupAnnountcement}
        toggleModal={() => setPopupAnnountcement({ shownPopupAnnountcement: false })}
      /> */}
    </DashboardLayoutProvider>
  );
}

DashboardLayout.Header = Header;
DashboardLayout.Sidebar = Sidebar;
DashboardLayout.Content = Content;

export default DashboardLayout;
